import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { Client } from "../../../Models/client";
import { AccountService } from "../../../Services/account.service";
import { ClientService } from "../../../Services/client.service";
import { debounceTime, switchMap } from "rxjs/operators";
import { ProjectTheme } from "src/app/Models/ProjectTheme";
import { AuthenticationService } from "src/app/Services/authentification/services/authentication.service";
import { PasswordTO } from "src/app/Models/PasswordTO";
import { LanguageService } from "src/app/Services/language.service";
import { StorageService } from "../../../Services/storage.service";

@Component({
  selector: "app-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.scss"],
  standalone: false
})
export class ProfileComponent implements OnInit {
  private formatDate(date: string | Date | null | undefined): string {
    if (!date) {
      return "";
    }

    const d = new Date(date);

    const formatted = d.toLocaleDateString(this.dateLocale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    // Majuscule sur le mois (optionnel)
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  /**
   * snackbar component params object
   */
  showSnackbar: boolean = false;
  snackbarParams: { theme: string; message: string } = {
    theme: "",
    message: "",
  };

  client!: Client;
  userInfoList: { name: string; label: string; value: string }[] = [];

  phoneNumber: string | null = null;
  bank: ProjectTheme = "WUnivers";
  languesOptions: { label: string; value: string }[] = [];
  private destroyRef = inject(DestroyRef);
  private languageService = inject(LanguageService);

  /** Locale of the "member since" date shown by lib-my-account (the page is kept on language change). */
  get dateLocale(): string {
    return this.languageService.isArabic ? "ar-DZ" : "fr-FR";
  }
  showPwdChangedPopup: boolean = false;
  currentUserPasswordState: boolean = false;
  isLoginModalVisible: boolean = false;

  constructor(
    private storage: StorageService,
    public accountService: AccountService,
    private clientService: ClientService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.accountService.accountSubject.subscribe({
      next: (client) => {
        if (client) {
          this.client = client;
          this.setUserInformation();
        } else {
          console.warn("Aucun client retourné par l’API.");
        }
      },
      error: (err) => {
        console.error(err);
      },
    });

    // emits once translations are loaded, then on every language change
    this.translate
      .stream("account.profile.languages")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((languages: Record<string, string>) => {
        this.languesOptions = [
          { label: languages["en"], value: "En" },
          { label: languages["ar"], value: "Ar" },
          { label: languages["fr"], value: "Fr" },
        ];
        this.setUserInformation();
      });
  }

  /**
   * @description set the current user info to be displayed
   */
  setUserInformation() {
    if (!this.client) {
      return;
    }
    this.userInfoList = [
      {
        name: "firstName",
        label: this.translate.instant("account.profile.fullName"),
        value: this.client.firstname,
      },
      {
        name: "lastName",
        label: this.translate.instant("account.profile.fullName"),
        value: this.client.lastname,
      },
      {
        name: "email",
        label: this.translate.instant("account.profile.email"),
        value: this.client.email,
      },
      {
        name: "phone",
        label: this.translate.instant("account.profile.phone"),
        value: this.client.phone ?? "",
      },
      {
        name: "createdAt",
        label: this.translate.instant("account.profile.memberSince"),
        value: this.formatDate(this.client.createdAt),
      },
    ];
  }

  /**
   * @description update user information
   * @param {any} formClient the new data provided by the client
   */
  updateUserInformation(formClient: any) {
    this.client.firstname = formClient.firstName;
    this.client.lastname = formClient.lastName;
    this.client.phone = formClient.phone;

    this.clientService.changeInfo(this.client).subscribe({
      next: () => {
        this.setUserInformation();
        this.snackbarParams = {
          theme: "success",
          message: this.translate.instant("account.profile.updateSuccess"),
        };
        this.showSnackbar = true;

        setTimeout(() => {
          this.showSnackbar = false;
        }, 3000);
      },
      error: (err) => {
        this.snackbarParams = {
          theme: "alert",
          message: this.translate.instant("account.profile.updateError"),
        };
        this.showSnackbar = true;

        setTimeout(() => {
          this.showSnackbar = false;
        }, 3000);
      },
    });
  }

  /**
   * @description handle logout after updating user's password
   */
  onLogout(): void {
    this.authenticationService.logout().subscribe({
      next: () => {
        this.storage.setItem("passwordChanged", "true");
        this.router.navigateByUrl("/");
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  /**
   * @description emit an API to verify if the typed password by the user is the correct current user password
   * @param {string} typedPassword the password typed by the user
   */
  verifyCurrentPassword(typedPassword: string) {
    if (this.client.id) {
      this.clientService
        .checkPassword(this.client?.id, typedPassword)
        .pipe(debounceTime(3000))
        .subscribe({
          next: (response: boolean) => {
            this.currentUserPasswordState = response;
          },
        });
    }
  }

  /**
   * @description update user's password
   * @param {PasswordTO} passwordTO old and current password object
   */
  updateUserPassword(passwordTO: PasswordTO) {
    this.clientService.changePassword(passwordTO).subscribe({
      next: () => {
        this.onLogout();
      },
      error: () => {
        this.snackbarParams = {
          theme: "alert",
          message: this.translate.instant(
            "account.profile.passwordChangeError",
          ),
        };
        this.showSnackbar = true;

        setTimeout(() => {
          this.showSnackbar = false;
        }, 3000);
      },
    });
  }

  /**
   * @description go to login modal
   */
  goToLoginModal() {
    this.isLoginModalVisible = true;
  }

  /**
   * @description close the login modal
   */
  closeLoginModal() {
    this.isLoginModalVisible = false;
  }
}
